import { FORBIDDEN_MUTATION_TOOLS, FORBIDDEN_SAVE_TOOLS, getToolNamesSet, getToolsForLLM } from './tools/registry';

export function buildSystemPrompt(batch = 2) {
  const tools = getToolsForLLM(batch);
  return `你是低代码画布编辑器的 AI 助手。根据用户自然语言，输出 JSON 操作计划。

规则：
1. 只使用下列 Tool。禁止生态/矩阵/交互包。禁止切页、新建/复制/删除页面、改其它页、改工程全局宽高。
2. 只改当前正在编辑的这一页。跳转/指令/互斥是给当前组件自动绑定已有字段，不要切换编辑器页面。
3. 只修改组件模板里已有的字段，禁止发明新 key，禁止用整页 JSON 覆盖。
4. 输出必须是合法 JSON：{"summary":"中文摘要","steps":[{"tool":"tool.name","args":{}}]}
5. 不要在 steps 里包含 plan.preview / plan.apply / plan.discard
6. 组件 ref 可用 id 或 style.alias
7. 按钮类型：button_square / button_round / button_round_angle
8. 右侧基本属性、网络配置、事件、状态灯、组件联动里人手能改的，都可以用对应 Tool 改。
9. 用户框选了多个组件时，对齐/分布必须对 areaSelection 里的 id 使用 layout.align_* / layout.distribute_*，args.refs 填这些 id。不要对整页所有组件操作。
10. 用户说「选中的」时，ref 只能用上面给出的真实组件 id，必须写成字符串。禁止编造 id。只要上面已经给出锁定 id，就禁止回复找不到/未找到组件。
11. 改按钮显示文字用 style.set_button_text；改别名用 style.set_button_alias；改文字色/背景色用 style.set_button_colors（color、backgroundColor）。
12. 禁止输出 project.save / engine.flush_page / engine.persist_page。不要拼整页 JSON。保存由用户点「应用」走编辑器原保存。

可用 Tool（共 ${tools.length} 个）：
${JSON.stringify(tools, null, 0)}

仅返回 JSON，不要 markdown 代码块。`;
}

export function validatePlanShape(plan) {
  if (plan == null || typeof plan !== 'object' || Array.isArray(plan)) {
    return '计划必须是 JSON 对象';
  }
  if (!Array.isArray(plan.steps)) {
    return '缺少 steps 数组';
  }
  if (plan.summary != null && typeof plan.summary !== 'string') {
    return 'summary 必须是字符串';
  }
  const names = getToolNamesSet();
  const forbidden = {};
  FORBIDDEN_MUTATION_TOOLS.concat(FORBIDDEN_SAVE_TOOLS).forEach((t) => {
    forbidden[t] = true;
  });
  for (let i = 0; i < plan.steps.length; i++) {
    const step = plan.steps[i];
    if (!step || typeof step !== 'object' || Array.isArray(step)) {
      return `steps[${i}] 不是对象`;
    }
    const tool = step.tool || step.name;
    if (!tool || typeof tool !== 'string') {
      return `steps[${i}] 缺少 tool`;
    }
    if (forbidden[tool]) {
      return `不允许的操作: ${tool}`;
    }
    if (!names.has(tool)) {
      return `未知 tool: ${tool}`;
    }
    const args = step.args != null ? step.args : step.parameters;
    if (args != null && (typeof args !== 'object' || Array.isArray(args))) {
      return `steps[${i}].args 必须是对象`;
    }
  }
  return null;
}

export function parsePlanResponse(text) {
  if (text == null || String(text).trim() === '') {
    return { ok: false, error: '模型未返回内容' };
  }
  let raw = String(text).trim();
  const fence = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence) raw = fence[1].trim();
  if (!(raw.charAt(0) === '{' && raw.charAt(raw.length - 1) === '}')) {
    const start = raw.indexOf('{');
    const end = raw.lastIndexOf('}');
    if (start < 0 || end <= start) {
      return { ok: false, error: '模型未返回 JSON 对象' };
    }
    raw = raw.slice(start, end + 1);
  }
  let plan;
  try {
    plan = JSON.parse(raw);
  } catch (e) {
    return { ok: false, error: `模型返回的 JSON 不合法：${e.message}` };
  }
  const shapeError = validatePlanShape(plan);
  if (shapeError) {
    return { ok: false, error: `模型返回的 JSON 格式不正确：${shapeError}` };
  }
  return { ok: true, plan };
}
